import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  ArrowLeft, Download, FileText, Presentation, BookOpen, ChevronRight,
  CheckCircle2, Info, Lightbulb, List, Layers, Eye
} from "lucide-react";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";

applyPlugin(jsPDF);

const loadPptxGen = () => {
  return new Promise((resolve, reject) => {
    if (window.PptxGenJS) { resolve(window.PptxGenJS); return; }
    const script = document.createElement("script");
    script.src = "https://cdn.jsdelivr.net/gh/gitbrent/PptxGenJS@3.11.0/dist/pptxgen.bundle.js";
    script.onload = () => resolve(window.PptxGenJS);
    script.onerror = () => reject(new Error("Error cargando PptxGenJS"));
    document.head.appendChild(script);
  });
};

const ManualesPage = ({ API, token }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [manuales, setManuales] = useState([]);
  const [manualActivo, setManualActivo] = useState(null);
  const [seccionActiva, setSeccionActiva] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchManuales(); }, []);

  const fetchManuales = async () => {
    try {
      const resp = await axios.get(`${API}/definiciones/manuales`, { headers });
      setManuales(resp.data);
    } catch { toast.error("Error al cargar manuales"); }
  };

  const abrirManual = async (id) => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/definiciones/manuales/${id}`, { headers });
      setManualActivo(resp.data);
      setSeccionActiva(0);
    } catch { toast.error("Error al cargar manual"); }
    setLoading(false);
  };

  // === PDF EXPORT ===
  const exportarPDF = (manual) => {
    const doc = new jsPDF();
    const color = hexToRgb(manual.color);
    const pageW = doc.internal.pageSize.getWidth();
    let y = 0;

    // Cover page
    doc.setFillColor(color.r, color.g, color.b);
    doc.rect(0, 0, pageW, 100, "F");
    doc.setFillColor(0, 0, 0, 0.15);
    doc.rect(0, 85, pageW, 15, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(28);
    doc.text(manual.modulo, pageW / 2, 40, { align: "center" });
    doc.setFontSize(14);
    doc.text("Manual de Usuario", pageW / 2, 55, { align: "center" });
    doc.setFontSize(10);
    doc.text("SIRET - Sistema de Recaudacion Tributaria Municipal", pageW / 2, 70, { align: "center" });
    doc.setFontSize(9);
    doc.text(`Generado: ${new Date().toLocaleDateString("es-VE")}`, pageW / 2, 95, { align: "center" });
    doc.setTextColor(51, 51, 51);
    y = 115;
    doc.setFontSize(11);
    doc.text(manual.descripcion, 20, y, { maxWidth: pageW - 40 });
    y += 15;

    // Table of contents
    doc.setFontSize(16);
    doc.setTextColor(color.r, color.g, color.b);
    doc.text("Contenido", 20, y);
    y += 10;
    doc.setFontSize(11);
    doc.setTextColor(51, 51, 51);
    manual.secciones.forEach((sec, i) => {
      doc.text(`${i + 1}. ${sec.titulo}`, 25, y);
      y += 7;
    });
    y += 10;

    // Sections
    manual.secciones.forEach((sec, idx) => {
      if (y > 240) { doc.addPage(); y = 20; }

      // Section header
      doc.setFillColor(color.r, color.g, color.b);
      doc.rect(15, y - 5, pageW - 30, 10, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(13);
      doc.text(`${idx + 1}. ${sec.titulo}`, 20, y + 2);
      y += 15;
      doc.setTextColor(51, 51, 51);

      // Description
      doc.setFontSize(10);
      doc.text(sec.contenido, 20, y, { maxWidth: pageW - 40 });
      y += 12;

      // Steps
      if (sec.pasos?.length) {
        doc.setFontSize(11);
        doc.setTextColor(color.r, color.g, color.b);
        doc.text("Pasos:", 20, y);
        y += 7;
        doc.setTextColor(51, 51, 51);
        doc.setFontSize(9);
        sec.pasos.forEach((paso, pi) => {
          if (y > 270) { doc.addPage(); y = 20; }
          doc.text(`  ${pi + 1}. ${paso}`, 25, y);
          y += 6;
        });
        y += 5;
      }

      // Fields table
      if (sec.campos?.length) {
        if (y > 230) { doc.addPage(); y = 20; }
        doc.setFontSize(11);
        doc.setTextColor(color.r, color.g, color.b);
        doc.text("Campos del Formulario:", 20, y);
        y += 3;
        doc.autoTable({
          startY: y,
          margin: { left: 20, right: 20 },
          head: [["Campo", "Descripcion", "Req."]],
          body: sec.campos.map(c => [c.nombre, c.descripcion, c.requerido ? "Si" : "No"]),
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [color.r, color.g, color.b] },
          columnStyles: { 2: { halign: "center", cellWidth: 15 } }
        });
        y = doc.lastAutoTable.finalY + 8;
      }

      // States
      if (sec.estados?.length) {
        if (y > 240) { doc.addPage(); y = 20; }
        doc.setFontSize(11);
        doc.setTextColor(color.r, color.g, color.b);
        doc.text("Estados:", 20, y);
        y += 3;
        doc.autoTable({
          startY: y,
          margin: { left: 20, right: 20 },
          head: [["Estado", "Descripcion"]],
          body: sec.estados.map(e => [e.estado, e.descripcion]),
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [color.r, color.g, color.b] }
        });
        y = doc.lastAutoTable.finalY + 8;
      }

      // Tips
      if (sec.tips?.length) {
        if (y > 250) { doc.addPage(); y = 20; }
        doc.setFillColor(255, 250, 230);
        const tipH = 6 + sec.tips.length * 5;
        doc.rect(20, y - 3, pageW - 40, tipH, "F");
        doc.setFontSize(9);
        doc.setTextColor(180, 130, 0);
        doc.text("Tips:", 25, y + 2);
        y += 6;
        sec.tips.forEach(tip => {
          if (y > 275) { doc.addPage(); y = 20; }
          doc.text(`  - ${tip}`, 25, y, { maxWidth: pageW - 55 });
          y += 5;
        });
        y += 8;
      }
    });

    // Footer on last page
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text("SIRET - Sistema de Recaudacion Tributaria Municipal", pageW / 2, 285, { align: "center" });

    doc.save(`Manual_${manual.modulo.replace(/\s/g, "_")}_${new Date().toISOString().split("T")[0]}.pdf`);
    toast.success("PDF descargado exitosamente");
  };

  // === PPTX EXPORT ===
  const exportarPPTX = async (manual) => {
    let PptxGenJS;
    try {
      PptxGenJS = await loadPptxGen();
    } catch {
      toast.error("Error cargando generador de PowerPoint");
      return;
    }
    const pptx = new PptxGenJS();
    pptx.layout = "LAYOUT_WIDE";
    const colorHex = manual.color.replace("#", "");

    // Slide 1: Cover
    let slide = pptx.addSlide();
    slide.addShape(pptx.shapes.RECTANGLE, { x: 0, y: 0, w: "100%", h: "45%", fill: { color: colorHex } });
    slide.addText(manual.modulo, { x: 0, y: 0.8, w: "100%", h: 1.2, fontSize: 36, color: "FFFFFF", align: "center", bold: true });
    slide.addText("Manual de Usuario", { x: 0, y: 2.0, w: "100%", h: 0.6, fontSize: 18, color: "FFFFFF", align: "center" });
    slide.addText("SIRET - Sistema de Recaudacion Tributaria Municipal", { x: 0, y: 2.7, w: "100%", h: 0.5, fontSize: 12, color: "DDDDDD", align: "center" });
    slide.addText(manual.descripcion, { x: 0.8, y: 4.0, w: "85%", h: 1.0, fontSize: 14, color: "555555" });
    slide.addText(`Generado: ${new Date().toLocaleDateString("es-VE")}`, { x: 0, y: 6.5, w: "100%", h: 0.4, fontSize: 10, color: "999999", align: "center" });

    // Slide 2: TOC
    slide = pptx.addSlide();
    slide.addShape(pptx.shapes.RECTANGLE, { x: 0, y: 0, w: "100%", h: 0.8, fill: { color: colorHex } });
    slide.addText("Contenido", { x: 0.5, y: 0.1, w: 8, h: 0.6, fontSize: 24, color: "FFFFFF", bold: true });
    manual.secciones.forEach((sec, i) => {
      slide.addText(`${i + 1}. ${sec.titulo}`, {
        x: 1.0, y: 1.3 + i * 0.6, w: 8, h: 0.5, fontSize: 16, color: "333333",
        bullet: { type: "number", numberStartAt: i + 1 }
      });
    });

    // Section slides
    manual.secciones.forEach((sec, idx) => {
      // Section title slide
      slide = pptx.addSlide();
      slide.addShape(pptx.shapes.RECTANGLE, { x: 0, y: 0, w: "100%", h: 0.8, fill: { color: colorHex } });
      slide.addText(`${idx + 1}. ${sec.titulo}`, { x: 0.5, y: 0.1, w: 10, h: 0.6, fontSize: 22, color: "FFFFFF", bold: true });
      slide.addText(sec.contenido, { x: 0.5, y: 1.2, w: "90%", h: 0.6, fontSize: 13, color: "666666" });

      // Steps
      if (sec.pasos?.length) {
        let stepY = 2.0;
        sec.pasos.forEach((paso, pi) => {
          if (stepY > 6.5) {
            slide = pptx.addSlide();
            slide.addShape(pptx.shapes.RECTANGLE, { x: 0, y: 0, w: "100%", h: 0.8, fill: { color: colorHex } });
            slide.addText(`${sec.titulo} (cont.)`, { x: 0.5, y: 0.1, w: 10, h: 0.6, fontSize: 18, color: "FFFFFF", bold: true });
            stepY = 1.2;
          }
          slide.addShape(pptx.shapes.OVAL, { x: 0.5, y: stepY, w: 0.35, h: 0.35, fill: { color: colorHex } });
          slide.addText(`${pi + 1}`, { x: 0.5, y: stepY, w: 0.35, h: 0.35, fontSize: 11, color: "FFFFFF", align: "center", valign: "middle" });
          slide.addText(paso, { x: 1.0, y: stepY, w: 10, h: 0.35, fontSize: 12, color: "333333", valign: "middle" });
          stepY += 0.5;
        });
      }

      // Fields table
      if (sec.campos?.length) {
        slide = pptx.addSlide();
        slide.addShape(pptx.shapes.RECTANGLE, { x: 0, y: 0, w: "100%", h: 0.8, fill: { color: colorHex } });
        slide.addText(`Campos - ${sec.titulo}`, { x: 0.5, y: 0.1, w: 10, h: 0.6, fontSize: 20, color: "FFFFFF", bold: true });
        const rows = [
          [
            { text: "Campo", options: { bold: true, color: "FFFFFF", fill: { color: colorHex }, fontSize: 11 } },
            { text: "Descripcion", options: { bold: true, color: "FFFFFF", fill: { color: colorHex }, fontSize: 11 } },
            { text: "Req.", options: { bold: true, color: "FFFFFF", fill: { color: colorHex }, fontSize: 11, align: "center" } }
          ],
          ...sec.campos.map(c => [
            { text: c.nombre, options: { fontSize: 10, bold: true } },
            { text: c.descripcion, options: { fontSize: 10 } },
            { text: c.requerido ? "Si" : "No", options: { fontSize: 10, align: "center" } }
          ])
        ];
        slide.addTable(rows, { x: 0.5, y: 1.2, w: 12, colW: [3, 7, 1.2], border: { pt: 0.5, color: "CCCCCC" }, rowH: 0.4, autoPage: true });
      }

      // States
      if (sec.estados?.length) {
        slide = pptx.addSlide();
        slide.addShape(pptx.shapes.RECTANGLE, { x: 0, y: 0, w: "100%", h: 0.8, fill: { color: colorHex } });
        slide.addText(`Estados - ${sec.titulo}`, { x: 0.5, y: 0.1, w: 10, h: 0.6, fontSize: 20, color: "FFFFFF", bold: true });
        sec.estados.forEach((est, i) => {
          const estColor = est.color.replace("#", "");
          slide.addShape(pptx.shapes.ROUNDED_RECTANGLE, { x: 0.5, y: 1.3 + i * 0.8, w: 2, h: 0.5, fill: { color: estColor }, rectRadius: 0.1 });
          slide.addText(est.estado, { x: 0.5, y: 1.3 + i * 0.8, w: 2, h: 0.5, fontSize: 13, color: "FFFFFF", align: "center", valign: "middle", bold: true });
          slide.addText(est.descripcion, { x: 2.8, y: 1.3 + i * 0.8, w: 8, h: 0.5, fontSize: 12, color: "555555", valign: "middle" });
        });
      }

      // Tips
      if (sec.tips?.length) {
        slide = pptx.addSlide();
        slide.addShape(pptx.shapes.RECTANGLE, { x: 0, y: 0, w: "100%", h: 0.8, fill: { color: colorHex } });
        slide.addText(`Tips - ${sec.titulo}`, { x: 0.5, y: 0.1, w: 10, h: 0.6, fontSize: 20, color: "FFFFFF", bold: true });
        slide.addShape(pptx.shapes.ROUNDED_RECTANGLE, { x: 0.3, y: 1.1, w: 12.5, h: 0.3 + sec.tips.length * 0.6, fill: { color: "FFF8E1" }, rectRadius: 0.1, line: { color: "FFD54F", width: 1 } });
        sec.tips.forEach((tip, i) => {
          slide.addText(`${tip}`, { x: 0.8, y: 1.3 + i * 0.6, w: 11.5, h: 0.5, fontSize: 12, color: "795548", bullet: { type: "bullet" } });
        });
      }
    });

    pptx.writeFile({ fileName: `Manual_${manual.modulo.replace(/\s/g, "_")}_${new Date().toISOString().split("T")[0]}.pptx` });
    toast.success("PowerPoint descargado exitosamente");
  };

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) } : { r: 0, g: 0, b: 0 };
  };

  // === VISTA DEL MANUAL ===
  if (manualActivo) {
    const sec = manualActivo.secciones[seccionActiva];
    return (
      <div className="space-y-4" data-testid="manual-visor">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => setManualActivo(null)} data-testid="btn-volver-manuales">
            <ArrowLeft className="w-5 h-5 mr-2" /> Volver
          </Button>
          <div className="flex-1">
            <h2 className="text-xl font-bold" style={{ color: manualActivo.color }}>{manualActivo.titulo}</h2>
            <p className="text-xs text-slate-500">{manualActivo.descripcion}</p>
          </div>
          <Button variant="outline" className="gap-2" onClick={() => exportarPDF(manualActivo)} data-testid="btn-descargar-pdf">
            <FileText className="w-4 h-4" /> PDF
          </Button>
          <Button variant="outline" className="gap-2" onClick={() => exportarPPTX(manualActivo)} data-testid="btn-descargar-pptx">
            <Presentation className="w-4 h-4" /> PowerPoint
          </Button>
        </div>

        <div className="grid grid-cols-12 gap-4">
          {/* Sidebar nav */}
          <div className="col-span-3">
            <Card>
              <CardContent className="p-2">
                <p className="text-xs font-semibold text-slate-400 uppercase px-2 py-1">Secciones</p>
                {manualActivo.secciones.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => setSeccionActiva(i)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2 ${
                      i === seccionActiva ? "text-white font-semibold" : "text-slate-600 hover:bg-slate-100"
                    }`}
                    style={i === seccionActiva ? { backgroundColor: manualActivo.color } : {}}
                    data-testid={`seccion-${i}`}
                  >
                    <ChevronRight className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{s.titulo}</span>
                  </button>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Content */}
          <div className="col-span-9 space-y-4">
            {sec && (
              <>
                {/* Section header */}
                <Card className="border-t-4" style={{ borderTopColor: manualActivo.color }}>
                  <CardContent className="p-5">
                    <h3 className="text-lg font-bold text-slate-800 mb-1">{sec.titulo}</h3>
                    <p className="text-sm text-slate-600">{sec.contenido}</p>
                  </CardContent>
                </Card>

                {/* Steps */}
                {sec.pasos?.length > 0 && (
                  <Card>
                    <CardContent className="p-5">
                      <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                        <List className="w-4 h-4" style={{ color: manualActivo.color }} /> Pasos a seguir
                      </h4>
                      <div className="space-y-2">
                        {sec.pasos.map((paso, pi) => (
                          <div key={pi} className="flex items-start gap-3">
                            <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5" style={{ backgroundColor: manualActivo.color }}>
                              {pi + 1}
                            </div>
                            <p className="text-sm text-slate-700 pt-0.5">{paso}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Fields */}
                {sec.campos?.length > 0 && (
                  <Card>
                    <CardContent className="p-5">
                      <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                        <Layers className="w-4 h-4" style={{ color: manualActivo.color }} /> Campos del Formulario
                      </h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr style={{ backgroundColor: manualActivo.color }}>
                              <th className="px-3 py-2 text-left text-white text-xs font-semibold">Campo</th>
                              <th className="px-3 py-2 text-left text-white text-xs font-semibold">Descripción</th>
                              <th className="px-3 py-2 text-center text-white text-xs font-semibold">Req.</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sec.campos.map((c, ci) => (
                              <tr key={ci} className={ci % 2 === 0 ? "bg-slate-50" : ""}>
                                <td className="px-3 py-2 font-medium text-slate-800">{c.nombre}</td>
                                <td className="px-3 py-2 text-slate-600">{c.descripcion}</td>
                                <td className="px-3 py-2 text-center">
                                  {c.requerido ? (
                                    <Badge className="bg-red-100 text-red-700 text-xs">Sí</Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-xs">No</Badge>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* States */}
                {sec.estados?.length > 0 && (
                  <Card>
                    <CardContent className="p-5">
                      <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                        <Info className="w-4 h-4" style={{ color: manualActivo.color }} /> Estados y Transiciones
                      </h4>
                      <div className="flex flex-wrap gap-3">
                        {sec.estados.map((est, ei) => (
                          <div key={ei} className="flex items-center gap-2 border rounded-lg px-3 py-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: est.color }} />
                            <div>
                              <span className="text-sm font-semibold text-slate-800">{est.estado}</span>
                              <p className="text-xs text-slate-500">{est.descripcion}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Tips */}
                {sec.tips?.length > 0 && (
                  <Card className="border-l-4 border-l-amber-400 bg-amber-50/30">
                    <CardContent className="p-5">
                      <h4 className="text-sm font-semibold text-amber-700 mb-2 flex items-center gap-2">
                        <Lightbulb className="w-4 h-4" /> Tips y Recomendaciones
                      </h4>
                      <ul className="space-y-1.5">
                        {sec.tips.map((tip, ti) => (
                          <li key={ti} className="flex items-start gap-2 text-sm text-amber-800">
                            <CheckCircle2 className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // === GRID DE MANUALES ===
  return (
    <div className="space-y-5" data-testid="manuales-page">
      <div>
        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-indigo-600" />
          Manuales de Usuario
        </h2>
        <p className="text-sm text-slate-500 mt-1">Seleccione un módulo para ver su manual, descargarlo en PDF o PowerPoint.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {manuales.map((m) => (
          <Card key={m.id} className="hover:shadow-lg transition-shadow border-t-4 group" style={{ borderTopColor: m.color }} data-testid={`manual-card-${m.id}`}>
            <CardContent className="p-5 space-y-3">
              <div className="flex items-start justify-between">
                <h3 className="font-bold text-slate-800">{m.modulo}</h3>
                <Badge variant="outline" className="text-xs">{m.total_secciones} secciones</Badge>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">{m.descripcion}</p>
              <div className="flex items-center gap-1.5 text-xs text-slate-400">
                <List className="w-3 h-3" />
                {m.total_pasos} pasos documentados
              </div>
              <div className="flex gap-2 pt-1">
                <Button size="sm" className="flex-1 gap-1.5 text-white" style={{ backgroundColor: m.color }} onClick={() => abrirManual(m.id)} data-testid={`btn-ver-${m.id}`}>
                  <Eye className="w-3.5 h-3.5" /> Ver
                </Button>
                <Button size="sm" variant="outline" className="gap-1.5" onClick={async () => {
                  setLoading(true);
                  try {
                    const resp = await axios.get(`${API}/definiciones/manuales/${m.id}`, { headers });
                    exportarPDF(resp.data);
                  } catch { toast.error("Error"); }
                  setLoading(false);
                }} data-testid={`btn-pdf-${m.id}`}>
                  <FileText className="w-3.5 h-3.5" /> PDF
                </Button>
                <Button size="sm" variant="outline" className="gap-1.5" onClick={async () => {
                  setLoading(true);
                  try {
                    const resp = await axios.get(`${API}/definiciones/manuales/${m.id}`, { headers });
                    exportarPPTX(resp.data);
                  } catch { toast.error("Error"); }
                  setLoading(false);
                }} data-testid={`btn-pptx-${m.id}`}>
                  <Presentation className="w-3.5 h-3.5" /> PPTX
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default ManualesPage;
